/**
 * Deutsche Kartentitel für jedes Chat-Werkzeug — 06.09.2026.
 *
 * Warum: Im Chat stand die Karte „Ticket List Open". `toolLabel()` in
 * tool-cards.ts kennt nur rund 40 Namen; alle anderen Werkzeuge bekamen den
 * englischen Toolnamen in Title-Case. Hier steht für JEDES registrierte
 * Werkzeug ein kurzer deutscher Titel — Substantiv-Stil, 1–3 Wörter, bei
 * Familien „Familie · Aktion".
 *
 * Quelle der Namen: `name: "…"` bzw. `<name>: llm.tool(` in tools/*.ts,
 * die Variablen-Schlüssel in tools/profile.ts und `heavy_task` aus
 * server/chat.ts. Der Test tests/frontend/werkzeug-namen.test.ts liest
 * dieselben Dateien — ein neues Werkzeug ohne Titel fällt dort auf.
 *
 * Die Einträge aus TOOL_LABELS (tool-cards.ts) sind 1:1 übernommen und
 * dürfen hier nicht umbenannt werden. Neue Einträge nennen Tickets
 * „Aufgaben", so wie die App.
 */

export const WERKZEUG_NAMEN: Readonly<Record<string, string>> = {
  // --- aus TOOL_LABELS (tool-cards.ts) 1:1 übernommen ---------------------
  route_directions: "Route",
  gmail_send: "E-Mail",
  gmail_search: "E-Mail-Suche",
  gmail_draft_create: "E-Mail-Entwurf",
  send_mail: "E-Mail",
  weather: "Wetter",
  play_music: "Musik",
  web_search: "Websuche",
  web_image_search: "Bildersuche",
  memory_search: "Notiz-Suche",
  gedaechtnis_herkunft: "Herkunft im Gedächtnis",
  gedaechtnis_bestaetigen: "Erinnerung bestätigt",
  memory_save: "Notiz",
  memory_delete: "Notiz löschen",
  contact_find: "Kontakt",
  contact_call: "Anruf",
  open_app: "App öffnen",
  open_url: "Link",
  show_map: "Karte",
  make_pdf: "PDF",
  generate_image: "Bild",
  generate_music: "Musik",
  edit_image: "Bild",
  calendar_create: "Termin",
  calendar_list: "Kalender",
  flight_search: "Flugsuche",
  drive_search: "Drive-Suche",
  read_file: "Datei lesen",
  timer_local: "Timer",
  human_design: "Human Design",
  essence_mirror: "Essenz-Spiegel",
  morgenmeditation: "Morgenmeditation",
  // 27.09.2026 (Ticket 2698): den fertigen Player starten.
  abspielen: "Abspielen",
  ticket_create: "Aufgabe angelegt",
  ticket_update: "Aufgabe geändert",
  ticket_list: "Aufgaben",
  ticket_link: "Aufgaben verknüpft",
  // 27.09.2026 (Tickets 2657/2671): kurz, normal oder ausführlich.
  antwortmodus_setzen: "Antwortweise",
  // 02.10.2026 (Ticket 2914): „schalte … ein“. Neutral benannt (Prüfbefund 05.10.): die Karte steht auch,
  // wenn das Werkzeug nur liest, zurückfragt oder ablehnt — „umgestellt“ behauptete dann zu viel.
  einstellung_aendern: "Einstellung",
  // 27.09.2026 (Ticket 2676): „sprich X wie Y aus“.
  aussprache_merken: "Aussprache",
  // 27.09.2026 (Ticket 2701): er hat sich verabschiedet.
  gespraech_beenden: "Gespräch beendet",
  vorschlag_verwerfen: "Vorschlag verworfen",
  // 28.09.2026 (Ticket 2770): ein Schlüssel in die verschlüsselte Ablage.
  schluessel_ablegen: "Schlüssel abgelegt",
  stimme_benennen: "Stimme gemerkt",
  telegram_nachricht: "Auf Telegram geschickt",
  // 06.10.2026 (Vespera Connect, zweite Richtung). Neutral benannt: die Karte steht auch, wenn
  // das Postfach aus ist oder die Grenze greift — „gesendet“ behauptete dann zu viel.
  connect_verlauf: "Connect-Verlauf",
  entwickler_nachricht: "Nachricht an den Entwickler",
  entwickler_postfach: "Entwickler-Postfach",
  // 07.10.2026 (Live-Connect): neutral — die Karte steht auch, wenn keine Antwort kam.
  entwickler_warten: "Warten auf den Entwickler",
  ticket_comment: "Aufgaben-Notiz",
  schedule_task: "Geplanter Auftrag",
  // 07.10.2026: neutral benannt — die Karte steht auch, wenn eine Sperre den Anruf verhindert hat.
  anruf_jetzt: "Anruf",
  // Ticket 2180 (09.09.2026): die zweite Meinung auf Zuruf.
  gegenprobe: "Gegenprobe geholt",
  stimmung_verlauf: "Stimmung nachgesehen",
  achtsamkeit_spiegel: "Achtsamkeit nachgesehen",
  heavy_task: "Hintergrund-Auftrag",
  project_file_get: "Projekt-Datei",
  project_file_put: "Projekt-Datei",
  project_create: "Projekt",
  project_list: "Projekte",
  // 08.09.2026 — Martin: „Erinnerungen sollten … ‚Aufgaben' heißen."
  reminder_create: "Aufgabe",
  reminder_list: "Aufgaben",
  alltag_search: "Alltag-Suche",
  chat_history_search: "Gesprächs-Suche",
  lange_texte: "Lange Texte",
  karten_lesen: "Karten lesen",
  faktura_customer_create: "Kunde",
  faktura_invoice_create: "Rechnung",
  calendar_delete: "Termin löschen",
  contact_create: "Kontakt",
  contact_list: "Kontakte",
  narrate_to_mp3: "Vertonung",
  make_magazine: "Magazin",
  analyze_video: "Video-Analyse",
  read_url: "Webseite gelesen",
  webseite_liste: "Webseiten",
  webseite_status: "Webseite angesehen",
  webseite_versionen: "Versionen",
  webseite_auftrag: "Auftrag an die Webseite",
  webseite_auftrag_stand: "Auftrag laeuft",
  webseite_auftrag_wiederholen: "Auftrag fortsetzen",
  webseite_veroeffentlichen: "Veroeffentlicht",
  webseite_zurueckrollen: "Zurueckgerollt",
  shorts_marken: "Shorts · Marken",
  shorts_vorgaben: "Shorts · Vorgaben",
  shorts_status: "Shorts · Stand",
  shorts_anlegen: "Short erzeugen",
  shorts_neu: "Short neu erzeugen",
  shorts_vorlegen: "Short vorlegen",
  panel_verbindung: "Panel-Verbindung",
  attach_file: "Anhang",

  // --- Aufgaben (Tickets heißen in der App „Aufgaben") ---------------------
  ticket_list_open: "Offene Aufgaben",
  ticket_roadmap: "Aufgaben-Überblick",
  ticket_apply: "Aufgabe freigeschaltet",
  ticket_get: "Aufgabe angesehen",
  // Ticket 2562: Martins offene Fragen im Gespräch.
  ticket_offene_fragen: "Offene Fragen",
  ticket_selbsttests: "Selbsttests aus Aufgabe",
  journal_zaehlung: "Journal gezählt",
  ticket_frage_beantworten: "Antwort abgelegt",

  // --- Hintergrund-Aufträge --------------------------------------------------
  task_delegate: "Auftrag übergeben",
  task_list: "Hintergrund-Aufträge",
  task_laeufe: "CLI-Läufe ansehen",
  task_cancel: "Auftrag abgebrochen",
  claude_task_server: "Server-Auftrag",
  shell_exec: "Server-Befehl",
  vespera_ausliefern: "Vespera ausgeliefert",
  vespera_testflight: "TestFlight-Bau angestoßen",

  // --- Gedächtnis, Selbstbild, Alltag ----------------------------------------
  erinnern: "Erinnern",
  alltag_detail: "Alltag-Aufnahme",
  alltag_lesen: "Alltag-Wortlaut",
  // 01.10.2026 (Ticket 2849): „Alltag starten“ / „Alltag beenden“.
  alltag_steuern: "Alltag-Aufnahme gesteuert",
  day_note: "Tagesnotiz",
  // Ticket 2331 — der stille Rueckkanal in der Kopfzeile.
  glocke_hinterlegen: "Hinweis hinterlegen",
  biography_entity_lookup: "Biografie-Nachschlag",
  biography_timeline: "Biografie-Zeitleiste",
  stammdaten_merken: "Stammdaten gemerkt",
  stammdaten_vergessen: "Stammdaten vergessen",
  thread_open: "Faden geöffnet",
  thread_close: "Faden geschlossen",
  thread_list: "Offene Fäden",
  // Ticket #2141 — die Absicht des Tages, aus dem Gespraech mit Maja.
  tagesabsicht_setzen: "Absicht festgehalten",
  anker_zeigen: "Ankermoment",
  anker_anlegen: "Ankermoment festgehalten",
  anker_liste: "Ankermomente",
  anker_bearbeiten: "Ankermoment geändert",
  environment_history: "Umweltdaten",
  environment_correlate: "Umwelt-Korrelation",
  whoop_vitals: "Vitaldaten",
  training_plan_zeigen: "Trainingsplan",
  training_plan_aendern: "Trainingsplan ändern",
  training_eintragen: "Training eingetragen",
  human_design_connection: "Human Design · Verbindung",
  vocab_add: "Vokabel gespeichert",
  vocab_due: "Fällige Vokabeln",
  vocab_result: "Vokabel geprüft",
  vocab_stats: "Vokabel-Fortschritt",
  morse_stand: "Morse-Stand",
  morse_merkwort: "Morse-Merkwort",

  // --- Selbstprüfung des Systems ---------------------------------------------
  werkzeug_pruefen: "Werkzeug-Prüfung",
  werkzeug_paket_anfordern: "Werkzeug-Paket anfordern",
  mandat_anfragen: "Mandat anfragen",
  entwickler_bestellen: "Bei Entwickler bestellen",
  lauf_warten: "Lauf wartet",
  // 27.10.2026 (B9): Zugangswerte ohne Modell-Kontext.
  browser_feld_ablegen: "Browser · Zugangswert vorgemerkt",
  zugangswerte_vorlegen: "Zugangswerte ablegen",
  // 28.10.2026 (B10b): abgelegten SES-Zugang in die Konfiguration des Postausgangs.
  ablage_in_konfig: "Postausgang schreiben",
  messwerte: "Messwerte",
  nutzer_lage: "Lage eines Nutzers",
  gedaechtnis_selbsttest: "Gedächtnis-Selbsttest",
  handlungen_nachsehen: "Handlungen",
  changelog: "Neuigkeiten",
  current_time: "Uhrzeit",

  // --- Nutzer, Profil, Kontakte ----------------------------------------------
  user_create: "Nutzer angelegt",
  profile_update: "Profil geändert",
  profile_get: "Profil",
  user_location_get: "Standort",
  contact_update: "Kontakt geändert",
  contact_delete: "Kontakt gelöscht",
  call: "Anruf vorbereitet",

  // --- Projekte und Projekt-Dateien ------------------------------------------
  project_set_active: "Aktives Projekt",
  project_update: "Projekt geändert",
  project_delete: "Projekt gelöscht",
  project_link: "Projekt verknüpft",
  project_unlink: "Verknüpfung gelöst",
  project_file_create: "Projekt-Datei angelegt",
  project_file_list: "Projekt-Dateien",
  project_file_update: "Projekt-Datei geändert",
  project_file_delete: "Projekt-Datei gelöscht",
  project_file_move: "Projekt-Datei verschoben",
  voice_memo_save: "Sprachnotiz gespeichert",

  // --- Medien, Recherche, Unterwegs ------------------------------------------
  mediathek_suche: "Mediathek-Suche",
  book_compile: "Buch erstellt",
  drucksache_erstellen: "Drucksache erstellt",
  fokus_videos: "Fokus-Videos",
  phone_play: "Musik am Handy",
  amazon_search: "Produktsuche",
  deep_research: "Tiefenrecherche",
  research_directory: "Verzeichnis-Recherche",
  live_translate: "Übersetzung",
  oebb_journey: "Zugverbindung",
  unterkunft_zeigen: "Unterkünfte",
  ical_list: "iCal-Termine",
  ask_confirm: "Rückfrage",
  deliver_text: "Liefertext",
  open_view: "Ansicht geöffnet",
  app_neu_laden: "Seite neu laden",

  // --- Google Workspace: E-Mail, Kalender, Drive -----------------------------
  gmail_read_message: "E-Mail gelesen",
  gmail_anhang_lesen: "Anhang gelesen",
  // 15.10.2026 (Karten, Welle 2b): „gesendet“ im Namen log über einem Versand, der auf Martins Ja wartet — der Name nennt
  // die Handlung, der Stand das Ergebnis („wartet auf dein Ja“ / „gesendet“).
  gmail_draft_send: "E-Mail-Entwurf senden",
  gmail_drafts_list: "E-Mail-Entwürfe",
  calendar_update: "Termin geändert",
  drive_create_folder: "Drive · Ordner angelegt",
  drive_move: "Drive · Datei verschoben",
  drive_upload: "Drive · Datei abgelegt",

  // --- Sheets · Docs · Forms -------------------------------------------------
  sheets_find: "Sheets · Tabelle gefunden",
  sheets_read: "Sheets · Tabelle gelesen",
  sheets_append_row: "Sheets · Zeile angefügt",
  sheets_insert_row: "Sheets · Zeile eingefügt",
  sheets_update_cell: "Sheets · Zelle geändert",
  sheets_delete_row: "Sheets · Zeile gelöscht",
  docs_find: "Docs · Dokument gefunden",
  docs_read: "Docs · Dokument gelesen",
  docs_append: "Docs · Text angefügt",
  docs_replace: "Docs · Text ersetzt",
  docs_create: "Docs · Dokument angelegt",
  forms_create: "Forms · Formular angelegt",
  forms_add_question: "Forms · Frage hinzugefügt",
  forms_get_info: "Forms · Formular",
  forms_get_responses: "Forms · Antworten",

  // --- Faktura (Panel) -------------------------------------------------------
  faktura_stats: "Faktura · Übersicht",
  faktura_articles: "Faktura · Artikelsuche",
  faktura_article_create: "Faktura · Artikel angelegt",
  faktura_customers: "Faktura · Kundensuche",
  faktura_customer_get: "Faktura · Kundendaten",
  faktura_customer_update: "Faktura · Kunde geändert",
  faktura_invoice_clone: "Faktura · Rechnung kopiert",
  faktura_invoice_pdf: "Faktura · Rechnungs-PDF",
  faktura_invoice_search: "Faktura · Rechnungssuche",
  faktura_invoice_get: "Faktura · Rechnung gelesen",
  // 05.10.2026 (Ticket 2996): kein Werkzeug, sondern die Familie der
  // Rechnungskarten eines Zuges (lib/rechnung-karte.ts) — der Kopf der Faltung.
  faktura_rechnung: "Rechnung",
  faktura_recurring_list: "Faktura · Abo-Rechnungen",
  faktura_recurring_generate: "Faktura · Abo-Rechnungen erzeugt",
  faktura_invoice_send: "Faktura · Rechnung gesendet",
  faktura_invoice_delete: "Faktura · Rechnung gelöscht",
  faktura_invoice_update: "Faktura · Rechnung geändert",
  faktura_invoice_paid: "Faktura · Rechnung bezahlt",
  faktura_invoice_items_list: "Faktura · Positionen",
  faktura_invoice_item_add: "Faktura · Position hinzugefügt",
  faktura_invoice_item_update: "Faktura · Position geändert",
  faktura_invoice_item_delete: "Faktura · Position gelöscht",
  faktura_mahnung: "Faktura · Mahnlauf",

  // --- Buchhaltung (Eingangsrechnungen) --------------------------------------
  expense_create: "Buchhaltung · Beleg verbucht",
  expense_list: "Buchhaltung · Belege",
  expense_search: "Buchhaltung · Belegsuche",
  expense_stats: "Buchhaltung · Übersicht",
  expense_analyze_upload: "Buchhaltung · Beleg ausgelesen",

  // --- Newsletter (Panel) ----------------------------------------------------
  newsletter_templates: "Newsletter · Vorlagen",
  newsletter_recipients: "Newsletter · Empfänger",
  newsletter_stats: "Newsletter · Übersicht",
  newsletter_groups: "Newsletter · Gruppen",
  newsletter_senders: "Newsletter · Absender",
  newsletter_create: "Newsletter · Entwurf angelegt",
  newsletter_aus_vorlage: "Newsletter · Kampagne aus Vorlage",
  newsletter_lesen: "Newsletter · Aussendungen",
  newsletter_aendern: "Newsletter · Entwurf geändert",
  newsletter_send_test: "Newsletter · Testversand",
  newsletter_send: "Newsletter · Versand", // Welle 2a: auch die Vorschau ohne Ja läuft hier durch — „gestartet“ wäre gelogen
  newsletter_status: "Newsletter · Versandstand",

  // --- DNS (Cloudflare) ------------------------------------------------------
  dns_zones: "DNS · Domains",
  dns_list: "DNS · Einträge",
  dns_add: "DNS · Eintrag angelegt",
  dns_update: "DNS · Eintrag geändert",
  dns_delete: "DNS · Eintrag gelöscht",

  // --- Trello ----------------------------------------------------------------
  trello_create_card: "Trello · Karte angelegt",
  trello_list_cards: "Trello · Karten",
  trello_move_card: "Trello · Karte verschoben",
  trello_board_status: "Trello · Board-Übersicht",
  trello_create_board: "Trello · Board angelegt",
  trello_list_boards: "Trello · Boards",

  // --- System (Server, nur Superadmin) ---------------------------------------

  // --- Browser (Chrome-Erweiterung) ------------------------------------------
  browser_open_url: "Browser · Seite geöffnet",
  browser_tab_context: "Browser · Aktueller Tab",
  browser_get_active_tab: "Browser · Aktiver Tab",
  browser_get_page_text: "Browser · Seitentext",
  browser_screenshot: "Browser · Bildschirmfoto",
  browser_click: "Browser · Klick",
  browser_type: "Browser · Eingabe",
  // 06.10.2026 (Ticket 3023): alle Felder mit ihrem Wert, vor dem Absenden.
  browser_form_overview: "Browser · Formular geprüft",
  browser_keypress: "Browser · Tastendruck",
  // 22.10.2026 (Erweiterung 2.3.0): Mauszeiger, Ziehen, Datei ablegen.
  browser_hover: "Browser · Mauszeiger darüber",
  browser_drag: "Browser · Gezogen",
  browser_upload: "Browser · Datei hochladen",
  browser_scroll: "Browser · Gescrollt",
  browser_list_tabs: "Browser · Offene Tabs",
  browser_switch_tab: "Browser · Tab gewechselt",
  browser_form_submit: "Browser · Formular ausgefüllt",
  browser_close_tab: "Browser · Tab geschlossen",
  // 30.09.2026 (Ticket 2830): mehrere Schritte in einem Aufruf.
  browser_ablauf: "Browser · Mehrere Schritte",
  screen_ablauf: "Mac · Mehrere Schritte",

  // --- Mac (Bildschirm-Steuerung und Apps) -----------------------------------
  screen_context: "Mac · Überblick",
  screen_take_screenshot: "Mac · Bildschirmfoto",
  // 29.09.2026 (Ticket 2806): Blender über Vespera Desktop.
  blender_szene: "Blender · Szene gelesen",
  // 07.10.2026: neutral wie Render/Speichern (Prüfer-Befund 6) — „ausgeführt“ stand auch über einem Python-Fehler;
  // ob er lief, sagt die Zeile darunter (frontend/lib/blender-karten-text.ts).
  blender_python: "Blender · Befehl",
  blender_bild: "Blender · Ansicht",
  blender_nahaufnahme: "Blender · Nahaufnahme",
  // 06.10.2026 (Prüfer-Befund 6): neutral — die Karte steht auch über einem Fehlschlag („Gespeichert“ über
  // „nicht gespeichert“ wäre genau die Behauptung ohne Beleg, die der Vorfall haus.blend gezeigt hat).
  blender_render: "Blender · Render",
  // 07.10.2026: neutral wie Render/Speichern — die Karte steht auch über einem Fehlschlag.
  blender_pruefen: "Blender · Prüfung",
  blender_freigabe_zuruecknehmen: "Blender · Freigabe zurückgenommen",
  blender_aufsetzen: "Blender · Aufsetzen",
  blender_objekt_stellen: "Blender · Hinstellen",
  blender_herde_stellen: "Blender · Herde",
  modell_suchen: "Modelle · Suche",
  modell_importieren: "Modelle · Import",
  blender_speichern: "Blender · Speichern",
  // 11.10.2026: neutral wie Speichern/Render — die Karte steht auch über einem Fehlschlag („Geöffnet“ über „nicht geöffnet“ wäre
  // die Behauptung ohne Beleg).
  blender_oeffnen: "Blender · Datei öffnen",
  mac_anbindungen: "Mac · Programme anbinden",
  screen_look: "Mac · Bildschirmblick",
  screen_mouse_click: "Mac · Klick",
  screen_mouse_move: "Mac · Maus bewegt",
  screen_double_click: "Mac · Doppelklick",
  screen_right_click: "Mac · Rechtsklick",
  screen_type_text: "Mac · Eingabe",
  screen_hotkey: "Mac · Tastenkürzel",
  screen_scroll: "Mac · Gescrollt",
  screen_list_apps: "Mac · Laufende Apps",
  claude_task: "Mac · Auftrag",
  // 07.10.2026 (mehrere Geräte): Liste der verbundenen Macs und Browser bzw. die Wahl, wo Vespera arbeitet (S6: auch
  // Browser — darum ohne „Mac ·“).
  geraet_waehlen: "Gerät wählen",
  mac_numbers_table: "Mac · Numbers-Tabelle",
  numbers_table_to_mail: "Mac · Tabelle per Mail",
  mac_pages_doc: "Mac · Pages-Dokument",
  mac_keynote_present: "Mac · Keynote-Präsentation",
  mac_notes_create: "Mac · Notiz angelegt",
  mac_notes_append: "Mac · Notiz ergänzt",
  mac_notes_search: "Mac · Notizsuche",
  mac_reminders_create: "Mac · Erinnerung angelegt",
  mac_reminders_list: "Mac · Erinnerungen",
  mac_mail_compose: "Mac · Mail-Entwurf",
  mac_music: "Mac · Musik",
  mac_safari_open: "Mac · Safari geöffnet",
  mac_finder_reveal: "Mac · Im Finder gezeigt",
  mac_spotlight_search: "Mac · Spotlight-Suche",
  mac_dateien: "Mac · Dateien lesen",
  mac_dateien_aendern: "Mac · Dateien ändern",
  mac_rueckgaengig: "Mac · Rückgängig",
  mac_bedienung_lesen: "Mac · Knöpfe lesen",
  mac_bedienung_klicken: "Mac · Knopf drücken",
  mac_fenster_lesen: "Mac · Fenster lesen",
  mac_fenster_bedienen: "Mac · Fenster bedienen",
  mac_sicherung: "Mac · Sicherungspunkt",
  mac_kalender: "Mac · Kalender",
  mac_erinnerungen: "Mac · Erinnerungen",
  mac_notizen: "Mac · Notizen",
  mac_programme: "Mac · Programme und Fenster",
  mac_kurzbefehle: "Mac · Kurzbefehle",
  mac_zwischenablage: "Mac · Zwischenablage lesen und schreiben",
  mac_mitteilung: "Mac · Mitteilung",
  mac_telegram: "Mac · Telegram schreiben",
  mac_clipboard: "Mac · Zwischenablage",
  mac_system_volume: "Mac · Lautstärke",
  mac_applescript: "Mac · AppleScript",
};

/**
 * Kartentitel zu einem Werkzeugnamen. Unbekannt → Unterstriche zu
 * Leerzeichen, nur der erste Buchstabe groß („gibt_es_nicht" → „Gibt es
 * nicht") — kein Title-Case, das sah nach „Ticket List Open" aus.
 */
export function werkzeugName(tool: string): string {
  const bekannt = WERKZEUG_NAMEN[tool];
  if (bekannt) return bekannt;
  const roh = tool.replace(/_+/g, " ").trim();
  if (!roh) return "";
  return roh.charAt(0).toUpperCase() + roh.slice(1);
}
